import { Link2, Network, ShieldAlert, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/client/utils/errors';
import { useRefreshSeoBacklinks, useSeoBacklinksQuery, type SeoSite } from '@/client/api/marketing/seo';
import { EmptyState } from './components/marketing-ui';
import { MarketingPageShell } from './components/MarketingPageShell';
import {
  SITE_COLOR,
  SITE_LABEL,
  SeoError,
  SeoKpi,
  SeoKpiRow,
  SeoNote,
  SeoPanel,
  SeoTrend,
  TD,
  TH,
  TR,
  fmtNumber,
} from './components/seo-ui';

const SITES: SeoSite[] = ['feelyfeely', 'jiji', 'meqasa'];

/**
 * `/admin/seo/backlinks` — who links to us, Jiji and Meqasa, and the sites
 * linking to both competitors but not to us (ff-api service/seo_backlinks.py).
 */
export default function SeoBacklinksPage() {
  const { data, isLoading, error } = useSeoBacklinksQuery();
  const refresh = useRefreshSeoBacklinks();
  const latest = data?.latest;
  const ours = latest?.sites?.feelyfeely;

  const onRefresh = () => {
    if (window.confirm('Refresh backlinks now? This is a paid lookup of about $0.16, allowed once a day.')) {
      refresh.mutate({});
    }
  };

  return (
    <MarketingPageShell
      title="Backlinks"
      description="Who links to us, Jiji and Meqasa, and the sites that link to both competitors but not to us."
      crumbs={[{ label: 'SEO', href: '/admin/seo' }, { label: 'Backlinks' }]}
      icon={Link2}
      actions={
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={refresh.isPending || !!data?.refreshed_today}
        >
          {refresh.isPending ? 'Refreshing…' : data?.refreshed_today ? 'Refreshed today' : 'Refresh now'}
        </Button>
      }
    >
      <div className="space-y-4">
        {error && <SeoError>Could not load backlinks.</SeoError>}
        {refresh.isError && <SeoError>{getApiErrorMessage(refresh.error, 'The refresh did not go through.')}</SeoError>}
        {(latest?.failed?.length ?? 0) > 0 && (
          <SeoError>Part of the last refresh failed: {latest?.failed.join(', ')}.</SeoError>
        )}

        {!isLoading && !latest ? (
          <EmptyState label="No backlink snapshot yet. Refresh to take the first one." />
        ) : (
          <>
            <SeoKpiRow>
              <SeoKpi label="Domains linking to us" value={ours?.referring_domains} icon={Network} loading={isLoading} />
              <SeoKpi label="Links to us" value={ours?.backlinks} icon={Link2} loading={isLoading} />
              <SeoKpi
                label="Outreach targets"
                value={latest?.gaps.length}
                icon={Target}
                hint="Link to Jiji and Meqasa, not us"
                loading={isLoading}
              />
              <SeoKpi
                label="Spam score"
                value={ours?.backlinks_spam_score}
                icon={ShieldAlert}
                tone={(ours?.backlinks_spam_score ?? 0) >= 30 ? 'bad' : 'neutral'}
                hint={
                  latest?.ours_junk && !latest.split_partial
                    ? `0-100. ${fmtNumber(latest.ours_junk)} junk sites push it up; Google ignores them`
                    : '0-100, of the links to us'
                }
                loading={isLoading}
              />
            </SeoKpiRow>

            <SeoPanel title="Against the competition" subtitle={latest ? `Snapshot of ${latest.day}` : undefined} flush>
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className={TH}>Site</th>
                    <th className={cn(TH, 'text-right')}>Domain rank</th>
                    <th className={cn(TH, 'text-right')}>Linking domains</th>
                    <th className={cn(TH, 'text-right')}>Links</th>
                    <th className={cn(TH, 'text-right')}>Broken links</th>
                    <th className={cn(TH, 'text-right')}>Spam score</th>
                  </tr>
                </thead>
                <tbody>
                  {SITES.map((site) => {
                    const s = latest?.sites?.[site];
                    return (
                      <tr key={site} className={TR}>
                        <td className={cn(TD, site === 'feelyfeely' && 'font-semibold')}>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: SITE_COLOR[site] }} />
                            {SITE_LABEL[site]}
                          </span>
                        </td>
                        <td className={cn(TD, 'text-right tabular-nums')}>{fmtNumber(s?.rank)}</td>
                        <td className={cn(TD, 'text-right tabular-nums')}>{fmtNumber(s?.referring_domains)}</td>
                        <td className={cn(TD, 'text-right tabular-nums')}>{fmtNumber(s?.backlinks)}</td>
                        <td className={cn(TD, 'text-right tabular-nums')}>{fmtNumber(s?.broken_backlinks)}</td>
                        <td className={cn(TD, 'text-right tabular-nums')}>{fmtNumber(s?.backlinks_spam_score)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </SeoPanel>

            {(data?.history.length ?? 0) > 1 && (
              <SeoPanel title="Linking domains over time" subtitle="One reading per refresh. Each site on its own scale.">
                <div className="grid gap-4 md:grid-cols-3">
                  {SITES.map((site) => (
                    <div key={site}>
                      <p className="mb-1 text-xs font-medium text-gray-700">{SITE_LABEL[site]}</p>
                      <SeoTrend
                        small
                        height={140}
                        data={(data?.history ?? []).map((h) => ({ day: h.day, value: h[site] ?? null }))}
                        series={[{ key: 'value', name: SITE_LABEL[site], color: SITE_COLOR[site] }]}
                      />
                    </div>
                  ))}
                </div>
              </SeoPanel>
            )}

            <div className="grid gap-4 lg:grid-cols-2">
              <SeoPanel
                title="Outreach targets"
                subtitle="Sites linking to both Jiji and Meqasa but not to us, strongest first"
                flush
              >
                {(latest?.gaps.length ?? 0) === 0 ? (
                  <p className="px-4 py-3 text-sm text-gray-500">None in this snapshot.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        <th className={TH}>Domain</th>
                        <th className={cn(TH, 'text-right')}>Rank</th>
                        <th className={cn(TH, 'text-right')}>Links to Jiji</th>
                        <th className={cn(TH, 'text-right')}>Links to Meqasa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {latest?.gaps.map((g) => (
                        <tr key={g.domain} className={TR}>
                          <td className={TD}>
                            <a href={`https://${g.domain}`} target="_blank" rel="noreferrer" className="hover:underline">
                              {g.domain}
                            </a>
                          </td>
                          <td className={cn(TD, 'text-right tabular-nums')}>{fmtNumber(g.rank)}</td>
                          <td className={cn(TD, 'text-right tabular-nums')}>{fmtNumber(g.links?.jiji)}</td>
                          <td className={cn(TD, 'text-right tabular-nums')}>{fmtNumber(g.links?.meqasa)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </SeoPanel>

              <SeoPanel
                title="Who links to us"
                subtitle={
                  !latest
                    ? undefined
                    : latest.ours_junk === undefined
                      ? `${fmtNumber(latest.ours_total)} domains, strongest 100 shown`
                      : latest.split_partial
                        ? `${fmtNumber(latest.ours_junk)} of the 100 stored are junk and hidden. Refresh to sort all ${fmtNumber(latest.ours_total)}.`
                        : `${fmtNumber(latest.ours_real)} real domains, strongest 100 shown. ${fmtNumber(latest.ours_junk)} junk hidden.`
                }
                flush
              >
                {(latest?.ours.length ?? 0) === 0 ? (
                  <p className="px-4 py-3 text-sm text-gray-500">
                    {latest?.ours_junk ? 'No real linking domains in this snapshot, only junk.' : 'No linking domains found.'}
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        <th className={TH}>Domain</th>
                        <th className={cn(TH, 'text-right')}>Rank</th>
                        <th className={cn(TH, 'text-right')}>Links</th>
                        <th className={TH}>First seen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {latest?.ours.map((d) => (
                        <tr key={d.domain} className={TR}>
                          <td className={TD}>{d.domain}</td>
                          <td className={cn(TD, 'text-right tabular-nums')}>{fmtNumber(d.rank)}</td>
                          <td className={cn(TD, 'text-right tabular-nums')}>{fmtNumber(d.backlinks)}</td>
                          <td className={cn(TD, 'text-gray-600')}>{d.first_seen ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {(latest?.ours_junk_sample?.length ?? 0) > 0 && (
                  <p className="px-4 py-3 text-xs text-gray-500">
                    Junk hidden, e.g. {latest?.ours_junk_sample?.slice(0, 8).join(', ')}. Auto-generated &ldquo;SEO
                    checker&rdquo; and &ldquo;buy traffic&rdquo; sites that link to every site they scan.
                  </p>
                )}
              </SeoPanel>
            </div>
          </>
        )}

        <SeoNote>
          From DataForSEO's link index. Rank is DataForSEO's 0-1000 domain score, not a Google figure. A refresh is a
          paid lookup of about $0.16 and is allowed once a day; nothing refreshes on a schedule.
        </SeoNote>
      </div>
    </MarketingPageShell>
  );
}
